import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as CloseIcon, Print as PrintIcon, ArrowBack as ArrowBackIcon, Edit as EditIcon, Send as SendIcon } from '@mui/icons-material';
import * as quoteApi from '../../api/quotes';
import * as contractApi from '../../api/contracts';
import { Quote, SupplierRate } from '../../types/quote.types';
import SupplierSelectionModal from '../../components/Quote/SupplierSelectionModal';
import QuoteRejectionModal from '../../components/Quote/QuoteRejectionModal';
import toast from 'react-hot-toast';
import { getAllLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup.types';
import { displayError } from '../../utils/errorHandler';
import { validateContractCreation } from '../../utils/validation';

const QuoteViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [supplierSelectionOpen, setSupplierSelectionOpen] = useState(false);
  const [pricingConfirmationOpen, setPricingConfirmationOpen] = useState(false);
  const [contractCreationOpen, setContractCreationOpen] = useState(false);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierRate | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  
  // Load status options
  useEffect(() => {
    console.log('QuoteViewer: Status loading useEffect triggered');
    const loadStatuses = async () => {
      try {
        console.log('QuoteViewer: Fetching all lookups...');
        const allLookups = await getAllLookups();
        console.log('QuoteViewer: All lookups received:', allLookups);
        
        // Filter for UtilityServiceStatus lookups
        const statusLookups = allLookups.filter((lookup: Lookup) => 
          lookup.type === 'UtilityServiceStatus' && lookup.active
        );
        console.log('QuoteViewer: Filtered status lookups:', statusLookups);
        
        const statusValues = statusLookups.map((lookup: Lookup) => lookup.value);
        console.log('QuoteViewer: Status values:', statusValues);
        setStatusOptions(statusValues);
        
        // Set default to first status if available
        if (statusValues.length > 0) {
          setSelectedStatus(statusValues[0]);
          console.log('QuoteViewer: Default status set to:', statusValues[0]);
        } else {
          console.warn('QuoteViewer: No status options available! Check if UtilityServiceStatus lookups exist in admin panel.');
          toast.error('No contract status options found. Please add them in the admin panel.');
        }
      } catch (error: any) {
        console.error('QuoteViewer: Error loading status options:', error);
        console.error('QuoteViewer: Error details:', error.response?.data, error.message);
        displayError(error, 'Failed to load status options');
      }
    };
    loadStatuses();
  }, []);

  useEffect(() => {
    if (id) {
      loadQuote(id);
    }
  }, [id]);

  const loadQuote = async (quoteId: string) => {
    try {
      setLoading(true);
      const data = await quoteApi.getQuote(quoteId);
      console.log('Quote data loaded:', data);
      console.log('Property data:', data.property);
      console.log('Client data:', data.client);
      setQuote(data);
    } catch (err) {
      setError('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const handleSendQuote = async () => {
    if (!quote?._id) return;
    
    try {
      await quoteApi.sendQuote(quote._id);
      const updatedQuote = await quoteApi.getQuote(quote._id);
      setQuote(updatedQuote);
      toast.success('Quote sent successfully');
    } catch (error) {
      displayError(error, 'Failed to send quote');
    }
  };

  const handlePrint = async () => {
    if (!quote?._id) return;
    
    try {
      const clientName = quote.client?.companyName || 'Quote';
      await quoteApi.downloadQuotePdf(quote._id, clientName);
      toast.success('Quote PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      displayError(error, 'Failed to download quote PDF');
    }
  };

  const handleEdit = () => {
    navigate(`/utilities/quotes/${quote?._id}/edit`);
  };

  const handleDuplicateAndEdit = async () => {
    if (!quote) return;
    
    try {
      // Duplicate the quote and create a new draft
      const newQuoteData: any = {
        client: quote.client?._id || '',
        property: quote.property?._id,
        meterType: quote.meterType,
        meterIdentifier: quote.meterIdentifier,
        currentSupplier: quote.currentSupplier,
        supplierRates: quote.supplierRates,
        status: 'DRAFT' as const,
        dateOfQuotation: new Date().toISOString(),
        eac: quote.eac,
        aq: quote.aq
      };
      const newQuote = await quoteApi.createQuote(newQuoteData);
      toast.success('Quote duplicated successfully');
      navigate(`/utilities/quotes/${newQuote._id}/edit`);
    } catch (error) {
      console.error('Error duplicating quote:', error);
      displayError(error, 'Failed to duplicate quote');
    }
  };

  // Handle supplier selection
  const handleSupplierSelect = (supplier: SupplierRate) => {
    setSelectedSupplier(supplier);
    setSupplierSelectionOpen(false);
    setPricingConfirmationOpen(true);
  };

  // Handle pricing confirmation
  const handlePricingConfirm = async () => {
    if (!quote?._id || !selectedSupplier) return;
    
    try {
      const updatedQuote = await quoteApi.updateQuoteStatus(quote._id, {
        status: 'SELECTED',
        selectedSupplier: selectedSupplier // Store the selected supplier
      });
      setQuote(updatedQuote);
      setPricingConfirmationOpen(false);
      setContractCreationOpen(true);
      toast.success('Quote selected successfully');
    } catch (error) {
      displayError(error, 'Failed to select quote');
    }
  };

  // Handle contract creation
  const handleContractCreate = async () => {
    if (!quote?._id) return;
    
    // Use the stored selectedSupplier from the quote, or fall back to the state variable
    const supplierToUse = quote.selectedSupplier || selectedSupplier;
    
    // Client-side validation
    const validationResult = validateContractCreation({
      supplier: supplierToUse,
      status: selectedStatus,
      contractStartDate: new Date(),
      contractEndDate: supplierToUse ? new Date(
        Date.now() + (supplierToUse.contractPeriodMonths * 30 * 24 * 60 * 60 * 1000)
      ) : undefined
    });
    
    if (!validationResult.isValid) {
      // Display validation errors
      Object.entries(validationResult.errors).forEach(([field, message]) => {
        toast.error(message, { duration: 4000 });
      });
      return;
    }
    
    console.log('Creating contract with status:', selectedStatus);
    console.log('Available status options:', statusOptions);
    
    try {
      // Create contract using the contracts API with selected status
      // supplierToUse is guaranteed to be non-null by validation above
      await contractApi.createContractFromQuote(quote, supplierToUse!, selectedStatus);
      const updatedQuote = await quoteApi.updateQuoteStatus(quote._id, {
        status: 'COMPLETED'
      });
      setQuote(updatedQuote);
      setContractCreationOpen(false);
      toast.success('Contract created and quote completed successfully');
    } catch (error) {
      console.error('Contract creation error:', error);
      displayError(error, 'Failed to create contract');
    }
  };

  // Handle quote rejection
  const handleRejectQuote = async (quoteId: string, rejectionReason: string) => {
    try {
      const updatedQuote = await quoteApi.updateQuoteStatus(quoteId, {
        status: 'REJECTED',
        rejectionReason
      });
      setQuote(updatedQuote);
      setRejectionModalOpen(false);
      toast.success('Quote rejected successfully');
    } catch (error) {
      displayError(error, 'Failed to reject quote');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'info';
      case 'DOWNLOADED':
        return 'secondary';
      case 'ISSUED':
        return 'warning';
      case 'SELECTED':
        return 'primary';
      case 'COMPLETED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!quote) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">Quote not found</Alert>
      </Container>
    );
  }

  // Add a guard clause for missing client
  if (!quote.client) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">
          Error: Quote has no associated client
        </Typography>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        mt: 4, 
        mb: 4,
        backgroundColor: 'rgba(25, 25, 25, 0.8)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: 2,
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        color: 'white',
        p: 3
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/utilities/quotes')} 
            sx={{ 
              mr: 2,
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Quote {quote.quoteRef}
          </Typography>
          <Chip
            label={quote.status}
            color={getStatusColor(quote.status) as any}
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {quote.status === 'DRAFT' && (
            <>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleSendQuote}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                Send Quote
              </Button>
            </>
          )}
          {(quote.status === 'DRAFT' || quote.status === 'DOWNLOADED' || quote.status === 'ISSUED') && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleDuplicateAndEdit}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              Duplicate & Edit
            </Button>
          )}
          {(quote.status === 'ISSUED' || quote.status === 'DOWNLOADED') && (
            <ButtonGroup variant="contained">
              <Button
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => setSupplierSelectionOpen(true)}
                sx={{
                  backgroundColor: 'success.main',
                  '&:hover': {
                    backgroundColor: 'success.dark',
                  },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                Select Supplier
              </Button>
              <Button
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => setRejectionModalOpen(true)}
                sx={{
                  backgroundColor: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                  },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                Reject
              </Button>
            </ButtonGroup>
          )}
          {quote.status === 'SELECTED' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setContractCreationOpen(true)}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                backdropFilter: 'blur(4px)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            >
              Create Contract
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              backdropFilter: 'blur(4px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {/* Quote Details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            color: 'white'
          }}>
            <Typography variant="h6" gutterBottom>
              Client Information
            </Typography>
            <Typography><strong>Company:</strong> {quote.client?.companyName || 'N/A'}</Typography>
            <Typography><strong>Contact Name:</strong> {(() => {
              // Try multiple ways to access contact name
              const propertyContactName = quote.property?.contactName || (quote as any).property?.contactName;
              const clientContactName = quote.client?.contactName || (quote as any).client?.contactName;
              const clientFullName = quote.client?.firstName && quote.client?.lastName 
                ? `${quote.client.firstName} ${quote.client.lastName}` 
                : null;
              // Fallback to client ownerName for legacy records missing property contact info
              const clientOwnerName = (quote as any).client?.ownerName;
              
              return propertyContactName || clientContactName || clientFullName || clientOwnerName || 'N/A';
            })()}</Typography>
            <Typography><strong>Email:</strong> {(() => {
              // Try property contact email first, then client email
              const propertyContactEmail = (quote as any).property?.contactEmail;
              const clientEmail = (quote as any).client?.email;
              
              return propertyContactEmail || clientEmail || 'N/A';
            })()}</Typography>
            <Typography><strong>Phone:</strong> {(() => {
              // Try property contact phone first, then client phone
              const propertyContactPhone = (quote as any).property?.contactPhone;
              const clientPhone = (quote as any).client?.phoneNumber;
              
              return propertyContactPhone || clientPhone || 'N/A';
            })()}</Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            color: 'white'
          }}>
            <Typography variant="h6" gutterBottom>
              Property Information
            </Typography>
            <Typography><strong>Address:</strong> {
              quote.property?.address 
                ? typeof quote.property.address === 'string' 
                  ? quote.property.address 
                  : typeof quote.property.address === 'object' && quote.property.address !== null
                    ? `${(quote.property.address as any).line1 || ''} ${(quote.property.address as any).line2 || ''} ${(quote.property.address as any).city || ''} ${(quote.property.address as any).county || ''} ${(quote.property.address as any).postcode || ''}`.trim()
                    : 'N/A'
                : 'N/A'
            }</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ 
            p: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            color: 'white'
          }}>
            <Typography variant="h6" gutterBottom>
              Quote Details
            </Typography>
            <Typography><strong>Meter Type:</strong> {quote.meterType}</Typography>
            <Typography><strong>Meter Identifier:</strong> {quote.meterIdentifier}</Typography>
            {quote.eac && <Typography><strong>EAC:</strong> {quote.eac} kWh</Typography>}
            {quote.aq && <Typography><strong>AQ:</strong> {quote.aq} kWh</Typography>}
            <Typography><strong>Created:</strong> {quote.createdAt ? format(new Date(quote.createdAt), 'dd/MM/yyyy HH:mm') : 'N/A'}</Typography>
            {quote.issuedDate && (
              <Typography><strong>Issued:</strong> {format(new Date(quote.issuedDate), 'dd/MM/yyyy')}</Typography>
            )}
          </Paper>
        </Grid>

        {quote.supplierRates && quote.supplierRates.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ 
            p: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            color: 'white'
          }}>
              <Typography variant="h6" gutterBottom>
                Supplier Rates
              </Typography>
              <TableContainer>
                <Table sx={{ '& .MuiTableCell-root': { color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Day Rate (p/kWh)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Standing Charge (p/day)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Annual Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Contract Period</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quote.supplierRates.map((rate, index) => (
                      <TableRow key={index}>
                        <TableCell>{rate.supplierName}</TableCell>
                        <TableCell align="right">{rate.dayUnitRate}</TableCell>
                        <TableCell align="right">{rate.standingCharge}p</TableCell>
                        <TableCell align="right">£{rate.annualExpenditure?.toFixed(2)}</TableCell>
                        <TableCell align="right">{rate.contractPeriodMonths} months</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Supplier Selection Modal */}
      <SupplierSelectionModal
        open={supplierSelectionOpen}
        onClose={() => setSupplierSelectionOpen(false)}
        quote={quote}
        onSelectSupplier={handleSupplierSelect}
      />

      {/* Pricing Confirmation Modal */}
      <Dialog 
        open={pricingConfirmationOpen} 
        onClose={() => setPricingConfirmationOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Confirm Pricing</DialogTitle>
        <DialogContent sx={{ color: 'white' }}>
          {selectedSupplier && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Selected Supplier: {selectedSupplier.supplierName}</Typography>
              <Typography>Annual Cost: £{selectedSupplier.annualExpenditure?.toFixed(2)}</Typography>
              <Typography>Day Rate: {selectedSupplier.dayUnitRate}p/kWh</Typography>
              <Typography>Standing Charge: £{selectedSupplier.standingCharge?.toFixed(2)}/day</Typography>
              <Typography sx={{ mt: 2 }}>
                Are you sure you want to select this supplier and pricing?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setPricingConfirmationOpen(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePricingConfirm} 
            variant="contained" 
            color="primary"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              backdropFilter: 'blur(4px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            Confirm Selection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contract Creation Modal */}
      <Dialog 
        open={contractCreationOpen} 
        onClose={() => setContractCreationOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 25, 25, 0.8)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Create Contract</DialogTitle>
        <DialogContent sx={{ color: 'white' }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            A contract will be created automatically from this selected quote.
          </Typography>
          {(quote?.selectedSupplier || selectedSupplier) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Contract Details:</Typography>
              <Typography>Supplier: {(quote?.selectedSupplier || selectedSupplier)?.supplierName}</Typography>
              <Typography>Annual Cost: £{(quote?.selectedSupplier || selectedSupplier)?.annualExpenditure?.toFixed(2)}</Typography>
              <Typography>Contract Period: {(quote?.selectedSupplier || selectedSupplier)?.contractPeriodMonths} months</Typography>
            </Box>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': { color: 'primary.main' }
              }}
            >
              Contract Status *
            </InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Contract Status *"
              required
              disabled={statusOptions.length === 0}
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            >
              {statusOptions.length === 0 ? (
                <MenuItem value="">Loading statuses...</MenuItem>
              ) : (
                statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))
              )}
            </Select>
            {statusOptions.length === 0 && (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 1 }}>
                Loading available statuses from admin panel...
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setContractCreationOpen(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleContractCreate} 
            variant="contained" 
            color="primary"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              backdropFilter: 'blur(4px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            Create Contract
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quote Rejection Modal */}
      <QuoteRejectionModal
        open={rejectionModalOpen}
        onClose={() => setRejectionModalOpen(false)}
        quote={quote}
        onReject={handleRejectQuote}
      />
    </Container>
  );
};

export default QuoteViewer;
